import { CLPrimaryCustomLabel } from '@/components/ui-controls';
import CLButton from '@/components/ui-controls/default-ui-controls/button';
import CommonConstants from '@/constants/common-constants';
import {
  GET_ASSIGNED_PREF,
  GET_GLOBAL_PREF,
  INSERT_NOTIFICATION
} from '@/graphql/notfications.gql';
import {
  CREATE_SINGLE_RUNS_ASSIGNMENT,
  GET_RUN_CHECKLIST_DETAILS_LENGTH_QUERY,
  UPDATE_RUN_CHECKLIST_STATUS_MULTIPLE_MUTATION
} from '@/graphql/runs.gql';
import {
  markParentAsCompleted,
  markParentAsNotCompleted,
  markAllChildAsCompletedOrNotCompleted as markSelfAndAllChildAsCompletedOrNotCompleted
} from '@/services/runs/runs.service';
import { getUserColor } from '@/services/utils/utils.service';
import { useAutoSaveStore } from '@/stores/builder/checklist-auto-save-tracker.store';
import { useCustomStatusIndicatorsStore } from '@/stores/builder/settings/custom-status-indicators.store';
import { useOrganizationStore } from '@/stores/organization.store';
import { useProjectsStore } from '@/stores/projects.store';
import { usePublicRunStore } from '@/stores/public-run/public-run-checklist.store';
import { useRunChecklistStore } from '@/stores/run/run-checklist.store';
import { Checklist } from '@/types/checklist.type';
import {
  RunChecklist,
  WorkflowAssignment,
  WorkflowConfiguration
} from '@/types/run.type';
import { ChecklistStatusIndicator } from '@/types/status-indicators.type';
import {
  MutationFunctionOptions,
  OperationVariables,
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery
} from '@apollo/client';
import { Transition } from '@headlessui/react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useUserData, useUserId } from '@nhost/nextjs';
import axios, { AxiosError, CancelToken } from 'axios';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { shallow } from 'zustand/shallow';
import CLTooltip from '../../../utils/tooltip';
import PublicRunCommentsComponent from '../../comments/public-run-comments.component';
import RunCommentsComponent from '../../comments/run-comments.component';
import { RunChecklistTree } from '../../run-by-id.component';
import TaskDetailsPopup from './task-details.popup.component';
import RunTaskPhotoCheckButton from './run-task-photo-check-button.component';
import RunPhotoCheckBar from './run-photo-check-bar.component';
import { useRunPhotosStore } from '@/stores/run/run-photos.store';
import {
  findNotDoneIndicator,
  registerRunTaskStatus
} from '@/services/photo-check/run-task-status-registry';

export default function ClassicViewTaskListComponent({
  runComplete
}: {
  runComplete: boolean;
}) {
  // Variables
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [runCompleted, setRunCompleted] = useState<boolean>(false);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [headerWidths, setHeaderWidths] = useState<number[]>([]);
  const [columnWidth, setColumnWidth] = useState(0);

  // Store
  const [
    runChecklistTree,
    statusIndicators,
    customStatusIndicators,
    isPreview,
    isPublic,
    isBuilderPreview,
    isSingleResponse,
    runChecklistObject
  ] = useRunChecklistStore(
    s => [
      s.runChecklistTree,
      s.statusIndicators,
      s.customStatusIndicators,
      s.isPreview,
      s.isPublic,
      s.isBuilderPreview,
      s.isSingleResponse,
      s.runChecklistObject
    ],
    shallow
  );

  // Opened from Analytics: load this run's photos once, to show next to each task
  const loadRunPhotos = useRunPhotosStore(s => s.load);
  useEffect(() => {
    if (isSingleResponse && runChecklistObject?.id) {
      loadRunPhotos(runChecklistObject.id);
    }
  }, [isSingleResponse, runChecklistObject?.id]);

  // Store
  const [selectedOrganization] = useOrganizationStore(
    state => [state.selectedOrganization],
    shallow
  );

  const [projects] = useProjectsStore(state => [state.projects], shallow);

  // 1) measurement function
  const measureWidths = useCallback(() => {
    const widths = headerRefs.current.map(el => (el ? el.offsetWidth : 0));
    setHeaderWidths(widths);
    setColumnWidth(Math.max(...widths, 0));
  }, []);

  useEffect(() => {
    // Measure once on mount / when statusIndicators change
    measureWidths();

    // Debounced resize handler
    let resizeTimer: number;
    const handleResize = () => {
      // clear any pending measure
      clearTimeout(resizeTimer);
      // schedule a new one
      resizeTimer = window.setTimeout(() => {
        measureWidths();
      }, 100);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, [statusIndicators, measureWidths]);
  // console.log('statusIndicators:', statusIndicators);
  // console.log('customStatusIndicators:', customStatusIndicators);

  const userId = useUserId();
  const userData = useUserData();
  function isRunChecklistFullyCompleted(
    checklistTree: RunChecklistTree
  ): boolean {
    if (!checklistTree || !checklistTree?.children) {
      return false;
    }
    // console.log('checklistTree:', checklistTree);

    for (const child of checklistTree.children) {
      // If any task is not completed, return false
      if (!child.checklist.is_completed) {
        return false;
      }

      // Recursively check child tasks
      if (!isRunChecklistFullyCompleted(child)) {
        return false;
      }
    }

    // If all tasks are completed, return true
    return true;
  }

  // 1) Global browser setting (event_type IS NULL)
  const { data: globalData } = useQuery(GET_GLOBAL_PREF, {
    variables: { user_id: userId, org_id: selectedOrganization?.id },
    skip:
      !userId ||
      !selectedOrganization?.id ||
      !!selectedOrganization?.pricing_subscription?.pricing_plan
        ?.pricing_plan_type?.is_freeplan,
    fetchPolicy: 'network-only'
  });

  // 2) Assigned-to-you browser setting (filtered server‑side)
  // const { data: assignedData } = useQuery(GET_ASSIGNED_PREF, {
  //   variables: {
  //     user_id: userId,
  //     org_id: selectedOrganization?.id,
  //     event_type: 'completed'
  //   },
  //   skip:
  //     !userId ||
  //     !selectedOrganization?.id ||
  //     !!selectedOrganization?.pricing_subscription?.pricing_plan
  //       ?.pricing_plan_type?.is_freeplan,
  //   fetchPolicy: 'network-only'
  // });

  // mutation to insert notifications
  const [insertNotification] = useMutation(INSERT_NOTIFICATION);

  const globalBrowserEnabled =
    globalData?.user_notification_preferences[0]?.browser_enabled ?? false;

  // const assignedBrowserEnabled =
  //   assignedData?.user_notification_preferences[0]?.browser_enabled ?? false;

  // Lazy query for assignor's browser preference
  const [fetchAssignorPref, { data: assignorPrefData }] =
    useLazyQuery(GET_ASSIGNED_PREF);

  const assignorId = runChecklistObject?.runs_assignments?.[0]?.user?.id;

  // Ask for permission once when component mounts
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        // console.log('Notification permission:', permission);
      });
    }
  }, []);

  useEffect(() => {
    const isCompleted = isRunChecklistFullyCompleted(
      runChecklistTree as RunChecklistTree
    );
    // console.log('isCompleted:', isCompleted);
    // console.log('runComplete:', runComplete);

    setRunCompleted(isCompleted);

    if (!runComplete && isCompleted) {
      // Check if it changed from false -> true
      if (isCompleted) {
        setShowSuccessToast(true);

        if (globalBrowserEnabled) {
          // 2) native browser notification
          // fireNativeNotification(
          //   `${runChecklistTree?.checklist?.title} Checklist run is completed`,
          //   'Your checklist run has been completed.'
          // );
          // // console.log(runChecklistObject?.runs_assignments);
          // // 3) insert notification
          // insertNotification({
          //   variables: {
          //     user_id: userId,
          //     org_id: selectedOrganization?.id,
          //     project_id: projects[0]?.id, // or props.projectId
          //     event_type: 'completed',
          //     title: 'Checklist Completed',
          //     body: `Your checklist "${runChecklistTree?.checklist.title}" has been completed.`,
          //     notify_at: new Date().toISOString(),
          //     is_sent: true,
          //     created_by: userId
          //   }
          // }).catch(err => console.error('Insert notification error', err));
        }

        // ✅ Fetch assignor's notification preference
        if (
          assignorId &&
          runChecklistObject?.runs_assignments?.[0]?.user?.id !== userId &&
          !selectedOrganization?.pricing_subscription?.pricing_plan
            ?.pricing_plan_type?.is_freeplan
        ) {
          fetchAssignorPref({
            variables: {
              user_id: assignorId,
              org_id: selectedOrganization?.id,
              event_type: 'completed'
            }
          });
        }

        const timer = setTimeout(() => {
          setShowSuccessToast(false);
        }, 3700);

        return () => clearTimeout(timer);
      }
    }
  }, [runChecklistTree?.checklist?.is_completed]);

  // ✅ On assignorPrefData change — send notification to assignor if allowed.
  useEffect(() => {
    if (
      globalBrowserEnabled &&
      assignorId &&
      assignorPrefData?.user_notification_preferences?.[0]?.browser_enabled &&
      runChecklistObject?.runs_assignments?.[0]?.user?.id !== userId &&
      !selectedOrganization?.pricing_subscription?.pricing_plan
        ?.pricing_plan_type?.is_freeplan
    ) {
      insertNotification({
        variables: {
          user_id: assignorId,
          org_id: selectedOrganization?.id,
          project_id: projects[0]?.id,
          event_type: 'completed',
          title: 'Checklist Completed',
          body: `The checklist "${
            runChecklistTree?.checklist.title
          }" you assigned to ${
            userData?.displayName || userData?.email
          } has been completed.`,
          notify_at: new Date().toISOString(),
          is_sent: false,
          created_by: userId,
          run_id: runChecklistObject?.id
        }
      }).catch(err =>
        console.error('Insert notification error (assignor)', err)
      );

      // Optionally, fire native browser notification for assignor if they're the current user
      // if (assignorId === userId) {
      //   fireNativeNotification(
      //     'Assigned Checklist Completed',
      //     `The checklist you assigned has been completed by ${
      //       userData?.displayName || userData?.email
      //     }`
      //   );
      // }
    }
  }, [assignorPrefData]);

  return (
    <>
      <div
        className={
          'bg-white rounded-lg min-h-full flex flex-col' +
          (isPublic ? ' !mt-2 m-4 lg:m-6' : isPreview ? ' p-4 pt-2' : ' m-4') +
          (isBuilderPreview
            ? ' rounded-none overflow-auto rounded-br-lg !pt-0 pl-0 pr-2 h-[10vh] bg-white'
            : ' !min-h-[94%]')
        }
      >
        {isBuilderPreview && (
          <div
            className="flex justify-between w-full text-black/50 font-semibold items-center px-2 pl-0 py-2 pb-4 text-sm h-4 z-10 sticky top-0 bg-white"
            style={{
              left: 0, // Align to the left edge
              marginLeft: 'calc(-1 * var(--tw-container-left, 0px))', // Adjust for container padding
              marginRight: 'calc(-1 * var(--tw-container-left, 0px))' // Adjust for container padding
            }}
          >
            <div className="text-sm">Preview</div>
            {/* <div className='text-xs items-start justify-start italic'>Any changes you make here won&apos;t be saved!</div> */}
          </div>
        )}
        {/* Header */}
        <div
          className={
            'flex flex-nowrap gap-0 mb-4 font-medium sticky top-0 z-10 bg-white ' +
            (isBuilderPreview
              ? ' sticky !top-6 bg-white !gap-0 mb-0 z-10'
              : '') +
            (isPreview
              ? ''
              : ' px-4 rounded-t-lg pt-4 pb-4 border-b-[1px] border-gray-300') +
            (!isPreview && !isBuilderPreview && !isPublic && !isSingleResponse
              ? 'overflow-auto'
              : '')
          }
        >
          {/* Title */}
          <div
            className={
              'flex-1 text-base ' +
              (isBuilderPreview
                ? 'text-black/70 flex pb-1 border-b-2 border-gray-300 !min-w-[190px]'
                : '') +
              (isPreview || isSingleResponse
                ? ' pb-1 border-b-2 border-gray-300'
                : '')
            }
          >
            <div className="inline-flex flex-wrap items-center gap-2">
              <span
                className="inline"
                style={
                  isBuilderPreview
                    ? {
                        alignItems: 'center',
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                        maxHeight: 'calc(1.3em * 2)',
                        lineHeight: '1.3em'
                      }
                    : isSingleResponse
                    ? { padding: '0 0.75rem' }
                    : undefined
                }
              >
                {!isSingleResponse
                  ? runChecklistTree?.checklist?.title || ''
                  : 'Tasks'}
                {!isPreview && !isBuilderPreview && !isPublic && (
                  <span
                    className={`inline-flex items-center ml-2 text-gray-500 text-[11.5px] px-2 py-0 h-[1.25rem] mb-0.5 pb-0.5 rounded-xl border border-gray-400 whitespace-nowrap align-middle ${
                      runChecklistTree?.checklist?.workflow_configuration
                        ? 'bg-slate-100'
                        : 'bg-white'
                    }`}
                  >
                    {(() => {
                      const cfg =
                        runChecklistTree?.checklist?.workflow_configuration;

                      if (cfg) {
                        if (cfg.type === 'parallel') {
                          return 'Workflow Type: Parallel';
                        }
                        const rules = cfg.workflow_conditional_rules ?? [];
                        return rules.length
                          ? 'Workflow Type: Sequential – Conditional'
                          : 'Workflow Type: Sequential – Task-by-Task';
                      }

                      return runCompleted ? 'Completed' : 'In-Progress';
                    })()}
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* Status indicators */}
          {statusIndicators?.map((statusIndicator, index) => (
            <div
              key={index}
              ref={el => (headerRefs.current[index] = el)}
              style={{ width: columnWidth - 3 }}
              className={
                'hidden lg:block ' +
                (isBuilderPreview
                  ? 'max-w-[84px] min-w-[83px] pl-2 pr-0.5 text-sm text-black/70 pb-1 z-5 bg-white border-b-2 border-gray-300'
                  : 'min-w-[90px] max-w-[150px]') +
                (isPreview || isSingleResponse
                  ? ' pb-1 border-b-2 border-gray-300'
                  : '')
              }
            >
              <div className="flex items-center justify-center text-center">
                <span className="whitespace-pre-wrap overflow-hidden break-words">
                  {statusIndicator?.name}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* One camera for the whole run: the photo is matched to the tasks it shows */}
        {!isBuilderPreview && <RunPhotoCheckBar />}

        {showSuccessToast && !isPreview && !isBuilderPreview && !isPublic && (
          <>
            {/* Foreground Toast (Doesn't Move Other Components) */}
            {showSuccessToast && (
              <div className="fixed top-[70px] w-[80%] md:w-[70%] lg:w-[79%] left-1/2 md:left-[62%] lg:left-[61%] transform -translate-x-1/2 text-center bg-green-200 z-20 text-base px-4 py-3 rounded-lg shadow-lg animate-fade-in-out">
                <div className="fixed inset-0 flex items-center justify-center z-0 pointer-events-none">
                  <div className="absolute -left-[13%] w-screen h-screen">
                    {/* First Confetti */}
                    <DotLottieReact
                      src="/confetti1.json"
                      loop
                      autoplay
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '96%',
                        top: '39%',
                        left: 0
                      }}
                    />
                    {/* Second Confetti (Overlapping the first) */}
                    <DotLottieReact
                      src="/confetti2.json"
                      loop
                      autoplay
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '96%',
                        top: '39%',
                        left: 0
                      }}
                    />
                  </div>
                </div>
                {/* Success Message */}
                <span className="font-semibold">
                  {runChecklistObject?.name}
                </span>{' '}
                Completed 🎉
              </div>
            )}
          </>
        )}

        {showSuccessToast &&
          !isPreview &&
          !isBuilderPreview &&
          isPublic &&
          !isSingleResponse && (
            <>
              {/* Foreground Toast (Doesn't Move Other Components) */}
              {showSuccessToast && (
                <div className="fixed top-[70px] w-[90%] left-[5%] text-center bg-green-200 z-20 text-base px-4 py-3 rounded-lg shadow-lg animate-fade-in-out">
                  <div className="fixed inset-0 flex items-center justify-center z-0 pointer-events-none">
                    <div className="absolute -left-[50%] w-screen h-screen">
                      {/* First Confetti */}
                      <DotLottieReact
                        src="/confetti1.json"
                        loop
                        autoplay
                        style={{
                          position: 'absolute',
                          width: '200%',
                          height: '170%',
                          top: '3%',
                          left: 0
                        }}
                      />
                      {/* Second Confetti (Overlapping the first) */}
                      <DotLottieReact
                        src="/confetti2.json"
                        loop
                        autoplay
                        style={{
                          position: 'absolute',
                          width: '200%',
                          height: '170%',
                          top: '3%',
                          left: 0
                        }}
                      />
                    </div>
                  </div>
                  <span className="font-semibold">
                    {runChecklistObject?.name}
                  </span>{' '}
                  Completed 🎉
                </div>
              )}
            </>
          )}

        {/* List of tasks */}
        {runChecklistTree &&
          runChecklistTree?.children &&
          runChecklistTree?.children?.length > 0 && (
            <>
              <div
                className={
                  !isBuilderPreview && !isSingleResponse && !isPreview
                    ? 'flex-grow px-4 rounded-t-lg overflow-auto'
                    : ''
                }
              >
                <RenderRunChecklist
                  node={runChecklistTree}
                  parentDisplayNumber={undefined}
                  isBuilderPreview={isBuilderPreview}
                  headerWidths={headerWidths}
                  columnWidth={columnWidth}
                  depth={0}
                />
              </div>

              <div className="mt-auto py-6 pb-2">
                {/* Run checklist comments */}
                {runChecklistTree?.checklist?.run_checklist_task_id && (
                  <div>
                    {isPublic || (isSingleResponse && isPublic) ? (
                      <PublicRunCommentsComponent
                        runChecklistId={
                          runChecklistTree?.checklist?.run_checklist_task_id
                        }
                      />
                    ) : (
                      (isSingleResponse || !isPublic) && (
                        <RunCommentsComponent
                          runChecklistId={
                            runChecklistTree?.checklist?.run_checklist_task_id
                          }
                        />
                      )
                    )}
                  </div>
                )}
              </div>
            </>
          )}
      </div>
    </>
  );
}

function RenderRunChecklist({
  node,
  parentDisplayNumber,
  isBuilderPreview,
  isChild,
  headerWidths,
  columnWidth,
  depth = 0
}: {
  node: RunChecklistTree;
  parentDisplayNumber: string | undefined;
  isBuilderPreview?: boolean;
  isChild?: boolean;
  headerWidths: number[];
  columnWidth: number;
  depth: number;
}) {
  // For child branches, position a full-height absolute line
  const containerClasses = isChild ? 'relative pl-0' : '';
  const containerRef = useRef<HTMLDivElement>(null);
  const [lineHeight, setLineHeight] = useState<string>('100%');
  const [updateFlag, setUpdateFlag] = useState<boolean>(false);

  const [builderCustomStatusIndicators] = useCustomStatusIndicatorsStore(
    s => [s.builderCustomStatusIndicators],
    shallow
  );

  let offsetIncrement;
  let baseOffset;
  let effectiveOffset;

  if (isBuilderPreview) {
    // Horizontal offset constants (in px)
    // Horizontal offset constant per level (in px)
    offsetIncrement = 0; // pixels between each additional vertical line level
    baseOffset = 0; // position for first child

    effectiveOffset =
      depth <= 1 ? baseOffset : baseOffset + (depth - 1) * offsetIncrement;
  } else {
    // Horizontal offset constants (in px)
    // Horizontal offset constant per level (in px)
    offsetIncrement = 0; // pixels between each additional vertical line level
    baseOffset = -4; // position for first child

    effectiveOffset =
      depth <= 1 ? baseOffset : baseOffset + (depth - 1) * offsetIncrement;
  }

  const calculateHeight = () => {
    if (isChild && containerRef.current && node.children.length) {
      const lastNode =
        node.children.find(c => c.isLastNode) ||
        node.children[node.children.length - 1];
      const lastEl = containerRef.current.querySelector(
        `div[data-checklist-id="${lastNode.checklist.id}"]`
      ) as HTMLElement;
      if (lastEl) {
        const parentRect = containerRef.current.getBoundingClientRect();
        const childRect = lastEl.getBoundingClientRect();
        const rootFontSize =
          parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        const proposedHeightInRem =
          (childRect.top - parentRect.top + 34.5) / rootFontSize;
        setLineHeight(`${proposedHeightInRem}rem`);
        setUpdateFlag(prev => !prev);
      }
    }
  };

  const calculateBuilderHeight = () => {
    if (isChild && containerRef.current && node.children.length) {
      // Find the last child marked isLastNode (or fallback to final child)
      const lastNode =
        node.children.find(c => c.isLastNode) ||
        node.children[node.children.length - 1];
      // Locate its rendered element via data attribute

      const lastEl = containerRef.current.querySelector(
        `div[data-checklist-id="${lastNode.checklist.id}"]`
      ) as HTMLElement;

      if (lastEl) {
        const parentRect = containerRef.current.getBoundingClientRect();
        const childRect = lastEl.getBoundingClientRect();

        // Use root <html> font-size (usually 16px) to convert px → rem:
        const rootFontSize =
          parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        const heightInRem =
          (childRect.top - parentRect.top + 34.5) / rootFontSize;

        setLineHeight(`${heightInRem}rem`);
      }
    }
  };

  useEffect(() => {
    if (!isBuilderPreview) {
      // Initial calculation
      calculateHeight();
      // Re-run once after 500ms
      const timeoutId = setTimeout(calculateHeight, 500);
      return () => clearTimeout(timeoutId);
    }

    if (isBuilderPreview) {
      // Initial calculation
      calculateBuilderHeight();
      // Re-run once after 500ms
      const timeoutId = setTimeout(calculateBuilderHeight, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [
    node.children,
    isChild,
    isBuilderPreview,
    lineHeight,
    headerWidths,
    depth,
    builderCustomStatusIndicators
  ]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isChild) {
        if (!isBuilderPreview) calculateHeight();
        else calculateBuilderHeight();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isBuilderPreview, node.children, isChild, depth]);

  return (
    <>
      <div className={containerClasses} ref={containerRef}>
        {/* Dynamic vertical line that stops at last child */}
        {isChild && (
          <span
            className="absolute -left-1 w-[1.6px] bg-[#ffd393]"
            style={{
              left: `${effectiveOffset}px`,
              top: isBuilderPreview ? '-0.9rem' : '-0.9rem',
              height: lineHeight
            }}
          />
        )}
        {/* Loop all children  */}
        {node?.children?.map((childNode, index) => {
          return (
            <div
              className="child-wrapper"
              key={`${childNode.checklist.id}-${index}`}
            >
              <RenderEachRunChecklist
                node={childNode}
                displayNumber={(parentDisplayNumber || '') + (index + 1) + '.'}
                isBuilderPreview={isBuilderPreview}
                isChild={isChild}
                headerWidths={headerWidths}
                columnWidth={columnWidth}
                depth={depth}
                calculateParentHeight={calculateHeight}
                calculateBuilderParentHeight={calculateBuilderHeight}
              />

              {/* Does this node have children? */}
              {childNode?.children && childNode?.children?.length > 0 && (
                <div
                  className={
                    (depth === 0 ? ' pl-6 ' : ' pl-6 ') +
                    (isBuilderPreview
                      ? depth === 0
                        ? ' !pl-3'
                        : ' !pl-7'
                      : '')
                  }
                >
                  <RenderRunChecklist
                    node={childNode}
                    parentDisplayNumber={
                      (parentDisplayNumber || '') + (index + 1) + '.'
                    }
                    isBuilderPreview={isBuilderPreview}
                    isChild={true}
                    headerWidths={headerWidths}
                    columnWidth={columnWidth}
                    depth={depth + 1}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function RenderEachRunChecklist({
  node,
  displayNumber,
  isBuilderPreview,
  isChild,
  headerWidths,
  columnWidth,
  depth,
  calculateParentHeight,
  calculateBuilderParentHeight
}: {
  node: RunChecklistTree;
  displayNumber: string | undefined;
  isBuilderPreview?: boolean;
  isChild?: boolean;
  headerWidths: number[];
  columnWidth: number;
  depth: number;
  calculateParentHeight: () => void;
  calculateBuilderParentHeight: () => void;
}) {
  // Variables

  return (
    <>
      {node?.children && node?.children?.length > 0 ? (
        <>
          {/* Parent checklist */}
          <RenderParentRunChecklist
            checklist={node?.checklist}
            node={node}
            displayNumber={displayNumber}
            isBuilderPreview={isBuilderPreview}
            isChild={isChild}
            headerWidths={headerWidths}
            columnWidth={columnWidth}
            depth={depth}
            calculateParentHeight={calculateParentHeight}
            calculateBuilderParentHeight={calculateBuilderParentHeight}
          />
        </>
      ) : (
        <>
          {/* Child checklist */}
          <RenderChildRunChecklist
            checklist={node?.checklist}
            node={node}
            displayNumber={displayNumber}
            isBuilderPreview={isBuilderPreview}
            isChild={isChild}
            headerWidths={headerWidths}
            columnWidth={columnWidth}
            depth={depth}
            calculateParentHeight={calculateParentHeight}
            calculateBuilderParentHeight={calculateBuilderParentHeight}
          />
        </>
      )}
    </>
  );
}

function RenderParentRunChecklist({
  checklist,
  node,
  displayNumber,
  isBuilderPreview,
  isChild,
  headerWidths,
  columnWidth,
  depth,
  calculateParentHeight,
  calculateBuilderParentHeight
}: {
  checklist: RunChecklist;
  node: RunChecklistTree;
  displayNumber: string | undefined;
  isBuilderPreview?: boolean;
  headerWidths: number[];
  columnWidth: number;
  isChild?: boolean;
  depth: number;
  calculateParentHeight: () => void;
  calculateBuilderParentHeight: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  // States
  const [checklistDetails, setChecklistDetails] = useState<Checklist>();
  const { updatedAt, publishedAt } = useAutoSaveStore();

  // Store
  const [
    isPublic,
    customStatusIndicators,
    isPreview,
    statusIndicator,
    isSingleResponse
  ] = useRunChecklistStore(
    s => [
      s.isPublic,
      s.customStatusIndicators,
      s.isPreview,
      s.statusIndicators,
      s.isSingleResponse
    ],
    shallow
  );

  const [builderCustomStatusIndicators] = useCustomStatusIndicatorsStore(
    s => [s.builderCustomStatusIndicators],
    shallow
  );
  // console.log('builderCustomStatusIndicators:', builderCustomStatusIndicators);
  // console.log('customStatusIndicators:', customStatusIndicators);

  // console.log('parent checklist:', checklist);
  // console.log(
  //   'Out isBuilderPreview,isPreview,isSingleResponse:',
  //   isBuilderPreview,
  //   isPreview,
  //   isSingleResponse
  // );

  // Local state: Only show indicators that match the current checklist ID
  const [
    currentChecklistStatusIndicators,
    setCurrentChecklistStatusIndicators
  ] = useState<ChecklistStatusIndicator[]>([]);

  useEffect(() => {
    // console.log(
    //   'isBuilderPreview,isPreview,isSingleResponse:',
    //   isBuilderPreview,
    //   isPreview,
    //   isSingleResponse
    // );

    const source = isSingleResponse
      ? customStatusIndicators
      : isBuilderPreview || isPreview
      ? builderCustomStatusIndicators
      : customStatusIndicators;
    // console.log('source:', source);

    if (!source || !checklist?.id) return;

    const filtered = source.filter(
      s => s.checklist_id === (checklist.id || checklist.task_id)
    );
    setCurrentChecklistStatusIndicators(filtered);
  }, [
    builderCustomStatusIndicators,
    customStatusIndicators,
    checklist?.id,
    checklist?.task_id,
    isBuilderPreview,
    isPreview,
    isSingleResponse
  ]);

  const singleResponseCurrentChecklistStatusIndicators = useMemo(() => {
    if (!isSingleResponse || !customStatusIndicators || !checklist?.task_id) {
      return [];
    }

    return customStatusIndicators.filter(
      s => s.checklist_id === (checklist.id || checklist.task_id)
    );
  }, [isSingleResponse, customStatusIndicators, checklist?.task_id]);

  if (
    isSingleResponse &&
    currentChecklistStatusIndicators.length === 0 &&
    singleResponseCurrentChecklistStatusIndicators.length != 0
  ) {
    // console.log(
    //   'singleResponseCurrentChecklistStatusIndicators:',
    //   singleResponseCurrentChecklistStatusIndicators
    // );
    setCurrentChecklistStatusIndicators(
      singleResponseCurrentChecklistStatusIndicators
    );
  }

  // console.log(
  //   'currentChecklistStatusIndicators:',
  //   currentChecklistStatusIndicators
  // );

  // 1) grab siblings
  const siblings: RunChecklistTree[] = node.parent?.children || [];

  // 2) find the “previous” logical sibling
  const prevSibling = siblings.find(
    sib => sib.checklist.order_number === checklist.order_number! - 1
  );

  // 3) walk its subtree until you hit a node that has custom pills
  const sourceNode = findFirstWithCustom(
    prevSibling,
    builderCustomStatusIndicators || customStatusIndicators
  );
  // console.log('sourceNode:', sourceNode);

  // 4) finally extract the pills for rendering
  const parentCustomStatus: ChecklistStatusIndicator[] = (
    builderCustomStatusIndicators || customStatusIndicators
  ).filter(cs => cs.checklist_id === sourceNode?.checklist.id);

  if (
    parentCustomStatus.length > 0 &&
    currentChecklistStatusIndicators.length == 0
  ) {
    setCurrentChecklistStatusIndicators(statusIndicator);
  }

  // GraphQL
  const { loading, data, error, refetch } = useQuery<{
    checklist: Checklist[];
  }>(GET_RUN_CHECKLIST_DETAILS_LENGTH_QUERY, {
    variables: {
      checklistId: checklist?.id
    },
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!isPublic) {
      if (data?.checklist && data?.checklist?.length > 0) {
        setChecklistDetails(data?.checklist[0]);
      }

      if (!publishedAt || (updatedAt && updatedAt > publishedAt)) {
        refetch();
      }
    } else {
      const { token: cancelToken, cancel } = axios.CancelToken.source();

      fetchChecklistDetails(checklist?.id as string, cancelToken);

      return () => {
        cancel('Cancelled');
      };
    }
  }, [data, publishedAt, updatedAt]);

  useEffect(() => {
    if (!isBuilderPreview) {
      const timeoutId = setTimeout(calculateParentHeight, 500);
      return () => clearTimeout(timeoutId);
    } else {
      const timeoutId = setTimeout(calculateBuilderParentHeight, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, setIsOpen]);

  async function fetchChecklistDetails(
    checklistId: string,
    cancelToken: CancelToken
  ) {
    // Fetch all instruction mappings
    try {
      // Start fetching
      // setIsLoading(true);

      const payload = {
        checklistId: checklistId
      };

      const response = await axios.post(
        CommonConstants.API_PREFIX + '/public/run/get-checklist-instructions',
        payload,
        {
          cancelToken: cancelToken
        }
      );

      if (
        response?.data?.data?.checklist &&
        response?.data?.data?.checklist?.length > 0
      ) {
        setChecklistDetails(response?.data?.data?.checklist[0]);
      }

      // Stop fetching
      // setIsLoading(false);
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error?.response?.data?.error?.message || error.message);
      } else {
        console.error('Something Went wrong. Please try again');
      }
    }
  }

  let offsetIncrement;
  let baseOffset;
  let effectiveOffset;

  if (isBuilderPreview) {
    offsetIncrement = 0; // pixels between each additional vertical line level
    baseOffset = -15; // position for first child
    effectiveOffset =
      depth <= 1 ? baseOffset : baseOffset + (depth - 1) * offsetIncrement;
  } else {
    offsetIncrement = 0; // pixels between each additional vertical line level
    baseOffset = -4; // position for first child
    effectiveOffset =
      depth <= 1 ? baseOffset : baseOffset + (depth - 1) * offsetIncrement;
  }

  return (
    <>
      {/* Layout */}
      <div
        data-checklist-id={checklist.id}
        className={
          'relative group  min-w-fit rounded-lg ' +
          (isBuilderPreview
            ? ' my-2 py-2' + (isChild ? ' pl-4 ' : '')
            : ' my-2 py-2' + (isChild ? ' pl-0 ' : ''))
        }
      >
        {isChild && (
          // Horizontal line remains anchored to this div's position
          <span
            className={
              `relative flex top-3 h-[1.6px] bg-[#ffd393] transform -translate-y-1/2` +
              (isBuilderPreview ? ' w-[20px]' : ' w-[17px]')
            }
            style={{ left: `${effectiveOffset}px` }}
          />
        )}
        {/* Task title */}
        <div
          className={`flex items-center ${
            currentChecklistStatusIndicators?.length > 0
              ? ' flex-nowrap gap-0 mb-0 pb-1 font-medium lg:border-none lg:relative'
              : ''
          }`}
        >
          {/* custom bottom‐border line for lg screens, 4px inset */}
          {currentChecklistStatusIndicators?.length > 0 && (
            <span
              className="hidden lg:block absolute bottom-0 right-0 border-b-[1.5px] border-gray-300"
              style={{ left: '8px' }}
            />
          )}
          <RunChecklistTaskTitle
            checklist={node?.checklist}
            node={node}
            displayNumber={displayNumber}
            isParent={true}
            isBuilderPreview={isBuilderPreview}
            currentChecklistStatusIndicators={currentChecklistStatusIndicators}
          />
          {/* Custom Status indicators */}
          {currentChecklistStatusIndicators?.map((cs, i) => (
            <div
              key={i}
              style={{ width: columnWidth - 3 }}
              className={
                'hidden lg:block ' +
                (isBuilderPreview
                  ? 'max-w-[84px] min-w-[83px] pl-3 text-sm text-black/70 pb-1 z-5 bg-white'
                  : 'min-w-[90px] max-w-[150px]')
              }
            >
              <div className="flex items-center justify-center text-center">
                <span className="whitespace-pre-wrap overflow-hidden break-words">
                  {cs.name}
                </span>
              </div>
            </div>
          ))}

          {/* Toolbar */}
          {currentChecklistStatusIndicators?.length === 0 && (
            <div
              className={
                `flex items-center min-h-[20px] ml-2` +
                (isBuilderPreview ? ' !mr-3' : '')
              }
            >
              {((checklistDetails?.description?.length ?? 0) > 0 ||
                (checklistDetails?.checklist_component_mappings?.length ?? 0) >
                  0) && (
                <div className="block lg:group-hover:block">
                  <RunChecklistToolBar
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    node={node}
                    checklist={node?.checklist}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Popover Panel rendered at the correct location */}
      {/* {isOpen && ( */}
      <Transition
        show={isOpen}
        enter="transition ease-out duration-800"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-850"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <div className="relative bg-[#f1f5f9] mt-2 w-full rounded-lg">
          <TaskDetailsPopup
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            node={node}
            checklist={node?.checklist}
          />
        </div>
      </Transition>
      {/* )} */}

    </>
  );
}

function RenderChildRunChecklist({
  checklist,
  node,
  displayNumber,
  isBuilderPreview,
  isChild,
  headerWidths,
  columnWidth,
  depth,
  calculateParentHeight,
  calculateBuilderParentHeight
}: {
  checklist: RunChecklist;
  node: RunChecklistTree;
  displayNumber: string | undefined;
  isBuilderPreview?: boolean;
  isChild?: boolean;
  headerWidths: number[];
  columnWidth: number;
  depth: number;
  calculateParentHeight: () => void;
  calculateBuilderParentHeight: () => void;
}) {
  // Variables
  // console.log('child node:', node);
  // console.log('child checklist:', checklist);

  // Store
  const [runChecklistTree, runChecklistObject] = useRunChecklistStore(
    s => [s.runChecklistTree, s.runChecklistObject],
    shallow
  );

  // console.log(runChecklistTree);

  // Nhost hooks
  const userId = useUserId();

  const wfConfig = checklist.workflow_configuration;

  //
  // ————————————————————————————————————————————————————————
  //  Build a map: runStatusByTask[task_id] = true|false
  // ————————————————————————————————————————————————————————
  const runStatusByTask: Record<string, boolean> = {};
  runChecklistTree!.checklist.checklistsByChecklistId!.forEach(rc => {
    // rc.task_id is the foreign‑key back to your workflow_assignment.task_id
    runStatusByTask[rc.id!] = !!rc.is_completed;
  });
  // console.log('[unlock] runStatusByTask:', runStatusByTask);

  //
  // ————————————————————————————————————————————————————————
  //  Compute “sequentialUnlocked”:
  //    is every *earlier* task in your wfConfig.workflow_assignments done?
  // ————————————————————————————————————————————————————————
  let sequentialUnlocked = true;

  if (wfConfig?.type === 'sequence') {
    // pull the ordered list of task_ids
    const seq = wfConfig.workflow_assignments.map(wa => wa.task_id);
    // find our position by matching *task_id*
    const idx = seq.indexOf(checklist.id!);

    // console.log(
    //   `[unlock] thisChecklist.task_id=${
    //     checklist.id
    //   } at idx=${idx} in [${seq.join(',')}]`
    // );

    if (idx > 0) {
      const prior = seq.slice(0, idx);
      sequentialUnlocked = prior.every(tid => runStatusByTask[tid] === true);
      // console.log(
      //   `[unlock]    prior tasks [${prior.join(
      //     ','
      //   )}] all done? → ${sequentialUnlocked}`
      // );
    }
  }

  //
  // ————————————————————————————————————————————————————————
  //  Who’s allowed to tick this box?
  //   • must be assigned (or no one assigned)
  //   • must not be saving / globally disabled
  //   • if sequential, must be unlocked
  // ————————————————————————————————————————————————————————
  function findAssignedUsers(
    n: RunChecklistTree | undefined
  ): WorkflowAssignment[] {
    if (!n) return [];
    if (n.checklist.assignedUsers && n.checklist.assignedUsers.length > 0) {
      return n.checklist.assignedUsers;
    }
    return findAssignedUsers(n.parent);
  }

  /**
   * Can this task appear / be completed?
   * - If there’s a conditional rule for this task, evaluate that.
   * - Else if type=sequence, use sequentialUnlocked.
   * - Else (parallel) always unlocked.
   */
  const canUnlock = (taskId: string): boolean => {
    const rules = wfConfig?.workflow_conditional_rules || [];

    // 0) If this workflow is parallel, everything is open
    if (wfConfig?.type !== 'sequence') return true;

    // 1) Check if this task is a skip_to target in any conditional rule
    const targetingRules = rules.filter(r => r.skip_to_task_id === taskId);

    if (targetingRules.length > 0) {
      // This task has conditional rules - evaluate them (conditional logic has priority)
      const shouldShow = targetingRules.some(rule => {
        const conds = rule.workflow_conditional_rule_conditions ?? [];
        if (!conds.length) return true;

        let result = true;
        for (let i = 0; i < conds.length; i++) {
          const c = conds[i];

          // find the source task for this condition
          const sourceNode =
            runChecklistTree!.checklist.checklistsByChecklistId!.find(
              n => n.id === c.task_id
            );

          // Check if the condition is met
          let passed = false;
          if (c.checklist_status_indicators_id) {
            // Condition based on specific status indicator - must match AND be completed
            const savedIndicatorId = sourceNode?.checklist_status_indicators_id;
            const isCompleted = sourceNode?.is_completed || false;
            const statusMatches =
              savedIndicatorId === c.checklist_status_indicators_id;
            passed =
              c.operator === 'is'
                ? statusMatches && isCompleted
                : !(statusMatches && isCompleted);
          } else {
            // Condition based on completion status (is_completed)
            const isCompleted = sourceNode?.is_completed || false;
            passed = c.operator === 'is' ? isCompleted : !isCompleted;
          }

          if (i === 0) {
            result = passed;
          } else {
            // combine with previous via the prior joiner
            const prevJoiner = conds[i - 1].joiner;
            result = prevJoiner === 'AND' ? result && passed : result || passed;
          }
        }
        return result;
      });

      return shouldShow;
    }

    // 2) Check if this task is a source task in any conditional rule
    const isSourceTask = rules.some(rule =>
      rule.workflow_conditional_rule_conditions?.some(c => c.task_id === taskId)
    );

    if (isSourceTask) {
      // Source tasks should always be unlocked so they can be completed to trigger conditions
      return true;
    }

    // 3) For tasks not involved in conditional logic, follow sequence order
    const seqIds = wfConfig.workflow_assignments.map(w => w.task_id);
    const myIndex = seqIds.indexOf(taskId);

    // first task is always unlocked
    if (myIndex <= 0) return true;

    // require all *prior* tasks done
    return seqIds.slice(0, myIndex).every(id => runStatusByTask[id] === true);
  };

  // get the effective assignments
  const effectiveAssigned = findAssignedUsers(node);

  // allow if no one is assigned, or if current user *is* in the list
  const isAssignedToCurrentUser =
    effectiveAssigned.length === 0 ||
    effectiveAssigned.some(a => a.user_id === userId);

  // States
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [minWidthPx, setMinWidthPx] = useState(191);
  const [ownCustomStatus, setOwnCustomStatus] = useState<
    ChecklistStatusIndicator[]
  >([]);

  // Store
  const [
    statusIndicators,
    refreshRunChecklistTree,
    isPreview,
    isPublic,
    isStatusIndicatorDisabled,
    customStatusIndicators,
    isSingleResponse
  ] = useRunChecklistStore(
    s => [
      s.statusIndicators,
      s.refreshRunChecklistTree,
      s.isPreview,
      s.isPublic,
      s.isStatusIndicatorDisabled,
      s.customStatusIndicators,
      s.isSingleResponse
    ],
    shallow
  );

  // 4) anything else that can disable
  // console.log(
  //   `[disable-check] task ${checklist.task_id}:`,
  //   `assignedToMe=${isAssignedToCurrentUser}`,
  //   `isSaving=${isSaving}`,
  //   `statusDisabledFlag=${isStatusIndicatorDisabled}`,
  //   `seqWorkflow=${wfConfig?.type === 'sequence'}`,
  //   `seqUnlocked=${sequentialUnlocked}`
  // );

  const [builderCustomStatusIndicators] = useCustomStatusIndicatorsStore(
    s => [s.builderCustomStatusIndicators],
    shallow
  );
  // console.log('runChecklistTree:', runChecklistTree);

  // Store
  const [selectedOrganization] = useOrganizationStore(
    state => [state.selectedOrganization],
    shallow
  );

  const [projects] = useProjectsStore(state => [state.projects], shallow);

  const assignorId = runChecklistTree?.checklist?.runs_assignments?.[0]?.user
    ?.id as string;

  // 3) keep track of which users we've already notified
  const notifiedRef = useRef<Set<string>>(new Set());

  const [insertNotification] = useMutation(INSERT_NOTIFICATION);

  // flatten them
  const combinedStore = useMemo(
    () => [
      ...(builderCustomStatusIndicators || []),
      ...(customStatusIndicators || [])
    ],
    [builderCustomStatusIndicators, customStatusIndicators]
  );

  // ② this task’s own pills
  const ownCustom = useMemo(
    () => combinedStore.filter(cs => cs.checklist_id === checklist.id),
    [combinedStore, checklist.id]
  );

  // ③ nearest ancestor with pills (excluding the very top)
  const ancestorNode = useMemo(
    () => findNearestAncestorWithCustom(node, combinedStore),
    [node, combinedStore]
  );
  const ancestorCustom = ancestorNode
    ? combinedStore.filter(cs => cs.checklist_id === ancestorNode.checklist.id)
    : [];

  // console.log('Out isSingleResponse:', isSingleResponse);

  useEffect(() => {
    // console.log(
    //   'In isSingleResponse:',
    //   isSingleResponse,
    //   isBuilderPreview,
    //   isPreview
    // );
    const source = isSingleResponse
      ? customStatusIndicators
      : isBuilderPreview || isPreview
      ? builderCustomStatusIndicators
      : customStatusIndicators;

    if (!source || !checklist?.id) return;

    const parentFiltered = source.filter(s => s.checklist_id === checklist.id);
    setOwnCustomStatus(parentFiltered);
    // console.log(
    //   'customStatusIndicators:',
    //   customStatusIndicators,
    //   checklist,
    //   source
    // );

    // console.log('ownCustomStatus:', ownCustomStatus);
  }, [
    builderCustomStatusIndicators,
    customStatusIndicators,
    checklist?.id,
    checklist.parent_id,
    isBuilderPreview,
    isPreview,
    isSingleResponse
  ]);
  // console.log('runChecklistTree:', node.parent?.children);

  // console.log('child checklist:', checklist);
  // console.log('builderCustomStatusIndicators:', builderCustomStatusIndicators);

  // console.log('Out customStatusIndicators:', customStatusIndicators);

  // console.log('statusIndicators:', statusIndicators);

  // console.log(
  //   'currentChecklistStatusIndicators:',
  //   currentChecklistStatusIndicators
  // );

  // 1) grab siblings
  const siblings: RunChecklistTree[] = node.parent?.children || [];

  // 2) find the “previous” logical sibling
  const prevSibling = siblings.find(
    sib => sib.checklist.order_number === checklist.order_number! - 1
  );
  // console.log('prevSibling:', prevSibling?.checklist);

  // const combinedStore = [
  //   ...(builderCustomStatusIndicators || []),
  //   ...(customStatusIndicators || [])
  // ];
  // 3) walk its subtree until you hit a node that has custom pills
  const sourceNode = findFirstWithCustom(prevSibling, combinedStore);
  // console.log('sourceNode:', sourceNode);

  // 4) finally extract the pills for rendering
  const parentCustomStatus: ChecklistStatusIndicator[] = combinedStore.filter(
    cs => cs.checklist_id === sourceNode?.checklist.id
  );
  // console.log('parentCustomStatus:', parentCustomStatus);
  // console.log('child checklist:', checklist.title, checklist);
  // console.log('statusIndicators:', statusIndicators);
  // console.log('ownCustomStatus:', ownCustomStatus);
  // console.log(
  //   'currentChecklistStatusIndicators:',
  //   currentChecklistStatusIndicators
  // );
  // Decide which indicators to show:
  // 1) if this node has its own pills → use those
  // 2) else if an ancestor has pills → use those
  // 3) else → global
  const activeStatusIndicators =
    ownCustom.length > 0
      ? ownCustom
      : ancestorCustom.length > 0
      ? ancestorCustom
      : statusIndicators;

  const hasCustomSource = ownCustom.length > 0 || ancestorCustom.length > 0;
  // console.log('hasCustomSource:', hasCustomSource);
  // console.log('activeStatusIndicators:', activeStatusIndicators);

  // console.log(
  //   'checklist.title,activeStatusIndicators:',
  //   checklist.title,
  //   activeStatusIndicators
  // );

  const [publicRunAccessToken] = usePublicRunStore(
    s => [s.publicRunAccessToken],
    shallow
  );

  // GraphQL
  const [updateRunChecklistStatusMultipleMutation] = useMutation(
    UPDATE_RUN_CHECKLIST_STATUS_MULTIPLE_MUTATION
  );

  useEffect(() => {
    let w = 191; // depth 0
    if (depth === 1) w = 180;
    else if (depth === 2) w = 153;
    else if (depth === 3) w = 124;
    else if (depth === 4) w = 95;
    else if (depth >= 5) w = 88;

    setMinWidthPx(w);
  }, [depth]);

  async function onChangeOfStatusIndicator(
    statusIndicatorId: string,
    status: boolean,
    selectedRunNode: RunChecklistTree
  ) {
    if (selectedRunNode) {
      const updatableRunChecklist: RunChecklist[] = [];

      // Mark self and all child as completed or not-completed
      markSelfAndAllChildAsCompletedOrNotCompleted(
        selectedRunNode,
        status,
        updatableRunChecklist,
        statusIndicatorId
      );

      // Mark all the parents as not-completed
      if (status === false) {
        markParentAsNotCompleted(selectedRunNode.parent, updatableRunChecklist);
      } else {
        // Mark parent as completed, if all other child status is completed
        markParentAsCompleted(selectedRunNode.parent, updatableRunChecklist);
      }

      // Refresh the checklist view also
      refreshRunChecklistTree();

      // Don't update the status on database, if it is preview
      if (!isPreview && !isPublic) {
        await updateRunChecklistStatus(updatableRunChecklist);
      } else if (isPublic) {
        await updatePublicRunChecklistStatus(updatableRunChecklist);
      }
    }
  }

  // Let the photo check panel set this task's status through the same path as the checkbox
  const notDoneIndicator = findNotDoneIndicator(activeStatusIndicators);
  useEffect(() => {
    const taskId = node?.checklist?.task_id;
    if (!taskId || isBuilderPreview) return;
    return registerRunTaskStatus(taskId, {
      canChange: () =>
        !!isAssignedToCurrentUser &&
        !isSaving &&
        !isStatusIndicatorDisabled &&
        canUnlock(node?.checklist.id as string),
      isDone: () =>
        !!node?.checklist?.is_completed &&
        (!notDoneIndicator ||
          node?.checklist?.checklist_status_indicators_id !==
            notDoneIndicator.id),
      setDone: (done: boolean) =>
        onChangeOfStatusIndicator(
          activeStatusIndicators?.[0]?.id || '',
          done,
          node
        ),
      isNotDone: () =>
        !!notDoneIndicator &&
        !!node?.checklist?.is_completed &&
        node?.checklist?.checklist_status_indicators_id === notDoneIndicator.id,
      setNotDone: async () => {
        if (!notDoneIndicator?.id) return false;
        await onChangeOfStatusIndicator(notDoneIndicator.id, true, node);
        return true;
      },
      clearNotDone: async () => {
        if (!notDoneIndicator?.id) return;
        await onChangeOfStatusIndicator(notDoneIndicator.id, false, node);
      }
    });
  });

  // build a user lookup map once:
  const userMap = useMemo(() => {
    if (
      !runChecklistTree?.checklist.workflow_configuration &&
      node?.checklist?.assignedUsers?.length !== 0
    ) {
      return {};
    }
    const m: Record<string, { displayName?: string; email: string }> = {};
    const workflowConfig = runChecklistTree?.checklist
      ?.workflow_configuration as WorkflowConfiguration;
    if (workflowConfig?.workflow_assignments) {
      for (const a of workflowConfig.workflow_assignments) {
        m[a.user_id] = {
          displayName: a.user.displayName,
          email: a.user.email!
        };
      }
    }
    return m;
  }, [runChecklistTree]);

  // console.log('wfConfig:',wfConfig);
  // console.log('node:',node?.checklist);
  // console.log('runChecklistTree:',runChecklistTree?.checklist?.checklistsByChecklistId);

  // ─── 1) Helper: find all skip‐to checklist nodes whose source is completed ───
  const isSkippedToNode = (nodeId: string): boolean => {
    if (!wfConfig) return true;
    if (wfConfig.type !== 'sequence') return true;

    const rules = wfConfig.workflow_conditional_rules ?? [];
    if (!rules.length) return true;

    // All rules targeting this node
    const targetingRules = rules.filter(r => r.skip_to_task_id === nodeId);
    if (!targetingRules.length) return true;

    return targetingRules.some(rule => {
      const conds = rule.workflow_conditional_rule_conditions ?? [];
      if (!conds.length) return true;

      let result = true;
      for (let i = 0; i < conds.length; i++) {
        const c = conds[i];

        // find the RunChecklistTree node for this condition's task
        const sourceNode =
          runChecklistTree!.checklist.checklistsByChecklistId!.find(
            n => n.id === c.task_id
          );

        // Check if the condition is met based on status
        let passed = false;
        if (c.checklist_status_indicators_id) {
          // Condition based on specific status indicator - must match AND be completed
          const savedIndicatorId = sourceNode?.checklist_status_indicators_id;
          const isCompleted = sourceNode?.is_completed || false;
          const statusMatches =
            savedIndicatorId === c.checklist_status_indicators_id;
          passed =
            c.operator === 'is'
              ? statusMatches && isCompleted
              : !(statusMatches && isCompleted);
        } else {
          // Condition based on completion status (is_completed)
          const isCompleted = sourceNode?.is_completed || false;
          passed = c.operator === 'is' ? isCompleted : !isCompleted;
        }

        if (i === 0) {
          result = passed;
        } else {
          // combine with previous via the prior joiner
          const prevJoiner = conds[i - 1].joiner;
          result = prevJoiner === 'AND' ? result && passed : result || passed;
        }
      }

      return result;
    });
  };

  // console.log(
  //   node?.checklist?.id,
  //   checklist?.title,
  //   wfConfig,
  //   isSkippedToNode(node?.checklist?.id as string),
  //   !canUnlock(node?.checklist?.id as string)
  // );

  //
  // 1) Global toggle (event_type IS NULL)
  //
  const { data: globalPrefData } = useQuery(GET_GLOBAL_PREF, {
    variables: { user_id: assignorId, org_id: selectedOrganization?.id },
    skip:
      !assignorId ||
      !selectedOrganization?.id ||
      !!selectedOrganization?.pricing_subscription?.pricing_plan
        ?.pricing_plan_type?.is_freeplan,
    fetchPolicy: 'network-only'
  });

  const globalBrowserEnabled = useMemo(
    () =>
      globalPrefData?.user_notification_preferences[0]?.browser_enabled ??
      false,
    [globalPrefData]
  );

  //
  // 2) “task_complete_notify_assigner” preference for *current* user
  //
  const { data: taskCompletePrefData } = useQuery(GET_ASSIGNED_PREF, {
    variables: {
      user_id: assignorId,
      org_id: selectedOrganization?.id,
      event_type: 'task_complete_notify_assigner'
    },
    skip: !assignorId || !selectedOrganization?.id,
    fetchPolicy: 'network-only'
  });

  const allowTaskCompleteNotify = useMemo(
    () =>
      taskCompletePrefData?.user_notification_preferences[0]?.browser_enabled ??
      false,
    [taskCompletePrefData]
  );

  const client = useApolloClient();

  async function updateRunChecklistStatus(
    updatableRunChecklist: RunChecklist[]
  ) {
    setIsSaving(true);

    // Update completedBy
    updatableRunChecklist.map(runChecklist => {
      if (runChecklist?.is_completed) {
        runChecklist.completed_by = userId;
        runChecklist.completed_at = new Date().toISOString();
      } else {
        runChecklist.completed_by = undefined;
        runChecklist.completed_at = undefined;
      }

      return runChecklist;
    });

    // Insert and update
    const { data, errors } = await updateRunChecklistStatusMultipleMutation({
      variables: {
        runChecklist: updatableRunChecklist
      }
    });
    // console.log('checklist:', node?.checklist?.assignedUsers?.length);

    if (
      globalBrowserEnabled &&
      allowTaskCompleteNotify &&
      node?.checklist?.assignedUsers?.length !== 0 &&
      !runChecklistTree?.checklist?.is_completed &&
      Object.keys(userMap).length > 0
    ) {
      // 3) Figure out which task IDs are actually assigned in this workflow
      const assignedTaskIds =
        runChecklistTree!.checklist.workflow_configuration!.workflow_assignments!.map(
          a => a.task_id
        );
      // console.log('assignedTaskIds:', assignedTaskIds);

      // 4) See if any of the **nodes you just updated** is one of those assigned run‑task IDs…
      //    rather than using updatableRunChecklist, walk your tree directly:
      const touchedAssignedTask = runChecklistTree?.children.some(childNode =>
        assignedTaskIds.includes(childNode.checklist.id!)
      );
      // console.log('touchedAssignedTask:', touchedAssignedTask);

      if (
        !runChecklistTree?.checklist?.is_completed &&
        touchedAssignedTask &&
        !selectedOrganization?.pricing_subscription?.pricing_plan
          ?.pricing_plan_type?.is_freeplan
      ) {
        // now *immediately* check for “all done” and notify
        await notifyAssignersIfDone(
          runChecklistTree as RunChecklistTree,
          userId!,
          assignorId,
          insertNotification,
          projects[0]!.id,
          selectedOrganization?.id as string,
          notifiedRef.current,
          runChecklistObject?.name as string, // your runName
          userMap,
          runChecklistObject?.id as string // your runId
        );
      }
    }

    // If it’s a sequential workflow, try to hand off
    const wfConfig = checklist.workflow_configuration;
    if (wfConfig?.type === 'sequence') {
      for (const rc of updatableRunChecklist) {
        if (!rc.is_completed) continue;

        // build a Set of user_ids already assigned on this checklist
        const already = new Set(
          (checklist?.runs_assignments || []).map(a => a.user_id)
        );

        // console.log(
        //   `[handoff] checklist.id ${checklist.id} has already assigned:`,
        //   Array.from(already)
        // );

        // find the next user in the workflow assignment *sequence*
        const next = getNextSequentialAssignee(wfConfig, checklist.id!);
        if (!next) {
          // console.log(
          //   `[handoff] no further assignments for task ${checklist.id}`
          // );
          continue;
        }

        // guard against accidentally re-assigning the same user twice:
        if (already.has(next.user_id)) {
          // console.log(
          //   `[handoff] next.user_id ${next.user_id} is already in runs_assignments, skipping`
          // );
          continue;
        }

        // finally: create the new runs_assignment
        // console.log(
        //   `[handoff] assigning run ${rc.run_id} → user ${next.user_id}`
        // );
        const resp = await createSingleRunAssignment({
          variables: {
            runId: rc.run_id!,
            userId: next.user_id
          }
        });

        // 1) Global toggle (event_type IS NULL) — imperative client.query
        const { data: globalPrefData } = await client.query({
          query: GET_GLOBAL_PREF,
          variables: {
            user_id: next.user_id,
            org_id: selectedOrganization?.id
          },
          fetchPolicy: 'network-only'
        });
        const globalBrowserEnabled =
          globalPrefData?.user_notification_preferences?.[0]?.browser_enabled ??
          false;

        // 2) “task_assigned_to_you” preference
        const { data: taskAssignedPrefData } = await client.query({
          query: GET_ASSIGNED_PREF,
          variables: {
            user_id: next.user_id,
            org_id: selectedOrganization?.id,
            event_type: 'task_assigned_to_you'
          },
          fetchPolicy: 'network-only'
        });
        const allowTaskAssignedNotify =
          taskAssignedPrefData?.user_notification_preferences?.[0]
            ?.browser_enabled ?? false;

        if (
          globalBrowserEnabled &&
          allowTaskAssignedNotify &&
          !selectedOrganization?.pricing_subscription?.pricing_plan
            ?.pricing_plan_type?.is_freeplan
        ) {
          await insertNotification({
            variables: {
              user_id: next.user_id,
              org_id: selectedOrganization?.id,
              project_id: projects[0]!.id,
              event_type: 'task_assigned_to_you',
              title: 'Workflow Assigned',
              body: `You’ve been assigned to the "${runChecklistObject?.name}" workflow.`,
              notify_at: new Date().toISOString(),
              is_sent: next.user_id === userId,
              created_by: userId,
              run_id: rc.run_id
            }
          });
        }

        // console.log('[handoff] runs_assignment created:', resp.data);
      }
    }

    setIsSaving(false);
  }

  async function updatePublicRunChecklistStatus(
    updatableRunChecklist: RunChecklist[]
  ) {
    try {
      setIsSaving(true);

      // Update status
      const payload = {
        publicRunChecklist: updatableRunChecklist
      };

      // Resume the run
      const response = await axios.post(
        CommonConstants.API_PREFIX + '/public/run/update-status',
        payload,
        {
          headers: {
            authorization: 'Bearer ' + publicRunAccessToken
          }
        }
      );

      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);

      throw error;
    }
  }

  // States
  const [checklistDetails, setChecklistDetails] = useState<Checklist>();
  const { updatedAt, publishedAt } = useAutoSaveStore();

  // GraphQL
  const { loading, data, error, refetch } = useQuery<{
    checklist: Checklist[];
  }>(GET_RUN_CHECKLIST_DETAILS_LENGTH_QUERY, {
    variables: {
      checklistId: checklist?.id
    },
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!isBuilderPreview) {
      const timeoutId = setTimeout(calculateParentHeight, 500);
      return () => clearTimeout(timeoutId);
    } else {
      const timeoutId = setTimeout(calculateBuilderParentHeight, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, setIsOpen]);

  useEffect(() => {
    if (!isPublic) {
      if (data?.checklist && data?.checklist?.length > 0) {
        setChecklistDetails(data?.checklist[0]);
      }

      if (!publishedAt || (updatedAt && updatedAt > publishedAt)) {
        refetch();
      }
    } else {
      const { token: cancelToken, cancel } = axios.CancelToken.source();

      fetchChecklistDetails(checklist?.id as string, cancelToken);

      return () => {
        cancel('Cancelled');
      };
    }
  }, [data, updatedAt, publishedAt]);

  async function fetchChecklistDetails(
    checklistId: string,
    cancelToken: CancelToken
  ) {
    // Fetch all instruction mappings
    try {
      // Start fetching
      // setIsLoading(true);

      const payload = {
        checklistId: checklistId
      };

      const response = await axios.post(
        CommonConstants.API_PREFIX + '/public/run/get-checklist-instructions',
        payload,
        {
          cancelToken: cancelToken
        }
      );

      if (
        response?.data?.data?.checklist &&
        response?.data?.data?.checklist?.length > 0
      ) {
        setChecklistDetails(response?.data?.data?.checklist[0]);
      }

      // Stop fetching
      // setIsLoading(false);
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error?.response?.data?.error?.message || error.message);
      } else {
        console.error('Something Went wrong. Please try again');
      }
    }
  }

  let offsetIncrement;
  let baseOffset;
  let effectiveOffset;

  if (isBuilderPreview) {
    offsetIncrement = 0; // pixels between each additional vertical line level
    baseOffset = 0; // position for first child
    effectiveOffset =
      depth <= 1 ? baseOffset : baseOffset + (depth - 1) * offsetIncrement;
  } else {
    offsetIncrement = 0; // pixels between each additional vertical line level
    baseOffset = -4; // position for first child
    effectiveOffset =
      depth <= 1 ? baseOffset : baseOffset + (depth - 1) * offsetIncrement;
  }

  const [createSingleRunAssignment] = useMutation(
    CREATE_SINGLE_RUNS_ASSIGNMENT
  );

  function getNextSequentialAssignee(
    wf: WorkflowConfiguration,
    completedTaskId: string
  ): { task_id: string; user_id: string } | null {
    if (wf.type !== 'sequence') return null;

    // find the index of the *completed* task in the wf array
    const idx = wf.workflow_assignments.findIndex(
      wa => wa.task_id === completedTaskId
    );
    if (idx === -1) {
      // console.warn(
      //   `[handoff] completedTaskId ${completedTaskId} not in workflow_assignments`
      // );
      return null;
    }

    // the *next* workflow assignment is at idx+1
    const next = wf.workflow_assignments[idx + 1];
    if (!next) {
      // console.log(
      //   `[handoff] ${completedTaskId} was the last in sequence, no hand‑off`
      // );
      return null;
    }

    return next;
  }

  return (
    <>
      {/* 📍 if the previous sibling has any custom pills, render the global header exactly as in the parent */}
      {parentCustomStatus.length > 0 && (
        <div
          className={`hidden lg:flex items-center ml-1.5 my-1 py-1 ${
            parentCustomStatus.length > 0
              ? 'flex-nowrap gap-0 mb-1 font-medium border-b-[1.5px] border-gray-300'
              : ''
          }`}
        >
          <div
            className={
              'flex-1 invisible flex items-center relative' +
              (isBuilderPreview ? ` !min-w-[${minWidthPx}px]` : '')
            }
            style={
              isBuilderPreview ? { minWidth: `${minWidthPx}px` } : undefined
            }
          >
            {/* child title */}
            {/* <RunChecklistTaskTitle
              checklist={node.checklist}
              node={node}
              displayNumber={displayNumber}
              isParent={true} // same as parent style
              isBuilderPreview={isBuilderPreview}
              currentChecklistStatusIndicators={statusIndicators}
            /> */}
          </div>

          {/* global statusIndicators (not the custom ones) */}
          {activeStatusIndicators.map((si, idx) => (
            <div
              key={si.id ?? idx}
              style={{ width: columnWidth - 3 }}
              className={
                'hidden lg:block ' +
                (isBuilderPreview
                  ? 'max-w-[84px] min-w-[83px] pl-3 text-sm text-black/70 pb-1 z-5 bg-white'
                  : 'min-w-[90px] max-w-[150px]')
              }
            >
              <div className="flex items-center justify-center text-center">
                <span className="whitespace-pre-wrap overflow-hidden break-words">
                  {si.name}
                </span>
              </div>
            </div>
          ))}

          {/* if there were no custom pills, you’d show the toolbar—but here we skip that */}
        </div>
      )}

      {parentCustomStatus.length === 0 && ownCustomStatus.length !== 0 && (
        <div
          className={`hidden lg:flex items-center ml-1.5 my-1 py-1 ${
            parentCustomStatus.length === 0
              ? 'flex-nowrap gap-0 mb-1 font-medium border-b-[1.5px] border-gray-300'
              : ''
          }`}
        >
          <div
            className={
              'flex-1 invisible flex items-center relative' +
              (isBuilderPreview ? ` !min-w-[${minWidthPx}px]` : '')
            }
            style={
              isBuilderPreview ? { minWidth: `${minWidthPx}px` } : undefined
            }
          >
            {/* child title */}
            {/* <RunChecklistTaskTitle
                checklist={node.checklist}
                node={node}
                displayNumber={displayNumber}
                isParent={true} // same as parent style
                isBuilderPreview={isBuilderPreview}
                currentChecklistStatusIndicators={statusIndicators}
              /> */}
          </div>

          {/* global statusIndicators (not the custom ones) */}
          {activeStatusIndicators.map((si, idx) => (
            <div
              key={si.id ?? idx}
              style={{ width: columnWidth - 3 }}
              className={
                'hidden lg:block ' +
                (isBuilderPreview
                  ? 'max-w-[84px] min-w-[83px] pl-3 text-sm text-black/70 pb-1 z-5 bg-white'
                  : 'min-w-[90px] max-w-[150px]')
              }
            >
              <div className="flex items-center justify-center text-center">
                <span className="whitespace-pre-wrap overflow-hidden break-words">
                  {si.name}
                </span>
              </div>
            </div>
          ))}

          {/* if there were no custom pills, you’d show the toolbar—but here we skip that */}
        </div>
      )}

      {isSkippedToNode(node?.checklist?.id as string) && (
        <div
          data-checklist-id={checklist.id}
          className={
            'group  min-w-fit rounded-lg hover:bg-gray-100' +
            (isBuilderPreview ? ' my-2 py-2' : ' my-2 py-2')
          }
        >
          {isChild && (
            // Horizontal line remains anchored to this div's position
            <span
              className={
                `relative flex left-[2px] top-3 h-[1.6px] bg-[#ffd393] transform -translate-y-1/2` +
                (isBuilderPreview ? ' w-[21px]' : ' w-[17px]')
              }
              style={{ left: `${effectiveOffset}px` }}
            />
          )}
          {/* Task title and checkbox */}
          <div className="flex flex-nowrap gap-4 lg:gap-2 flex-col lg:flex-row">
            {/* Task title */}
            <div
              className={
                'flex-1 flex items-center relative' +
                (isBuilderPreview ? ` !min-w-[${minWidthPx}px]` : '')
              }
              style={
                isBuilderPreview ? { minWidth: `${minWidthPx}px` } : undefined
              }
            >
              <div className="flex items-start justify-start w-full gap-1">
                <RunChecklistTaskTitle
                  checklist={node?.checklist}
                  node={node}
                  displayNumber={displayNumber}
                  isParent={false}
                  isBuilderPreview={isBuilderPreview}
                  isChild={isChild}
                />

                {/* Toolbar */}
                <div className="min-h-[20px]">
                  {((checklistDetails?.description?.length ?? 0) > 0 ||
                    (checklistDetails?.checklist_component_mappings?.length ??
                      0) > 0) && (
                    <div className="block lg:group-hover:opacity-100 transition-opacity">
                      <RunChecklistToolBar
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                        node={node}
                        checklist={node?.checklist}
                        onChangeOfStatusIndicator={onChangeOfStatusIndicator}
                        isSaving={isSaving}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Status indicators for desktop screen */}
            <div className="hidden h-fit lg:flex gap-3 xl:gap-3">
              {activeStatusIndicators?.map((statusIndicator, index) => (
                <div
                  key={index}
                  style={{ width: columnWidth - 12 }}
                  className={
                    'flex justify-center ' +
                    (isBuilderPreview
                      ? 'min-w-[71px] max-w-[71px] text-sm'
                      : '')
                  }
                >
                  <input
                    type="checkbox"
                    className={
                      'bg-white border-black text-[#474554] focus:ring-black/20 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed' +
                      (isBuilderPreview ? ' w-5 h-5' : ' w-5 h-5')
                    }
                    checked={
                      (checklist?.is_completed &&
                        (statusIndicator.id ===
                          checklist.checklist_status_indicators_id ||
                          !statusIndicator.id)) ||
                      false
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      onChangeOfStatusIndicator(
                        statusIndicator?.id || '',
                        event.target.checked,
                        node
                      );
                    }}
                    disabled={
                      !isAssignedToCurrentUser ||
                      isSaving ||
                      isStatusIndicatorDisabled ||
                      // disable until _this_ node is unlocked
                      !canUnlock(node?.checklist.id as string)
                    }
                  />
                </div>
              ))}
            </div>

            {/* Status indicators for mobile screen */}
            <div className="flex lg:hidden gap-4 flex-col px-4">
              {activeStatusIndicators?.map((statusIndicator, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="checkbox"
                    id={
                      checklist?.id +
                      '-' +
                      statusIndicator?.id +
                      '-status-indicator'
                    }
                    className={
                      'bg-white border-black text-[#474554] focus:ring-black/20 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed' +
                      (isBuilderPreview ? ' w-5 h-5' : ' w-5 h-5')
                    }
                    checked={
                      (checklist?.is_completed &&
                        (statusIndicator.id ===
                          checklist.checklist_status_indicators_id ||
                          !statusIndicator.id)) ||
                      false
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      onChangeOfStatusIndicator(
                        statusIndicator?.id || '',
                        event.target.checked,
                        node
                      );
                    }}
                    disabled={
                      !isAssignedToCurrentUser ||
                      isSaving ||
                      isStatusIndicatorDisabled ||
                      // disable until _this_ node is unlocked
                      !canUnlock(node?.checklist.id as string)
                    }
                  />
                  <CLPrimaryCustomLabel
                    className="flex items-center cursor-pointer pt-[2px]"
                    htmlFor={
                      checklist?.id +
                      '-' +
                      statusIndicator?.id +
                      '-status-indicator'
                    }
                  >
                    {statusIndicator?.name}
                  </CLPrimaryCustomLabel>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Popover Panel rendered at the correct location */}
      {/* {isOpen && ( */}
      <Transition
        show={isOpen}
        enter="transition ease-out duration-800"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-850"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <div className="relative bg-[#f1f5f9] mt-2 w-full rounded-lg">
          <TaskDetailsPopup
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            node={node}
            checklist={node?.checklist}
          />
        </div>
      </Transition>
      {/* )} */}
    </>
  );
}

/**
 * If a “word” is longer than maxLen, insert soft-hyphens (\u00AD) every maxLen chars.
 */
function insertSoftHyphens(word: string, maxLen = 9) {
  if (word.length <= maxLen) return word;
  return word.replace(new RegExp(`(.{${maxLen}})`, 'g'), `$1\u00AD`);
}

function breakLongWords(title: string): string {
  return title
    .split(' ')
    .map(w => insertSoftHyphens(w, 9))
    .join(' ');
}

function RunChecklistTaskTitle({
  checklist,
  node,
  displayNumber,
  isParent,
  isBuilderPreview,
  isChild,
  currentChecklistStatusIndicators
}: {
  checklist: RunChecklist;
  node: RunChecklistTree;
  displayNumber: string | undefined;
  isParent: boolean;
  isBuilderPreview?: boolean;
  isChild?: boolean;
  currentChecklistStatusIndicators?: ChecklistStatusIndicator[];
}) {
  // Variables

  const rawTitle = checklist?.title || '';
  const titleWithSoftHyphens = breakLongWords(rawTitle);

  const assignedUsers = checklist?.assignedUsers ?? [];
  // console.log('assignedUsers:', assignedUsers);

  return (
    <>
      <div
        className={
          'flex flex-col gap-2 pl-4 ' +
          (isBuilderPreview
            ? ' opacity-70 !pl-2' + (isChild ? ' !pl-6 ' : '')
            : '') +
          ((currentChecklistStatusIndicators?.length ?? 0) > 0 ? ' flex-1' : '')
        }
      >
        {/* Title */}
        {/* Title + Pills as siblings */}
        <div className="flex flex-wrap items-start gap-2">
          {/* 1) Title: allow natural wrapping */}
          <span className="break-words">
            {displayNumber} {isParent ? checklist.title : titleWithSoftHyphens}
          </span>

          {/* Camera for this task; criteria come from the checklist title */}
          {!isBuilderPreview && <RunTaskPhotoCheckButton node={node} />}

          {/* 2) Pills: will flow inline until no room, then wrap */}
          {assignedUsers.map((u, idx) => {
            const user = u.user;
            const displayName = user?.displayName ?? '';
            const borderColor = getUserColor(user?.id || `${idx}`);
            return (
              <div
                key={user?.id ?? idx}
                title={displayName}
                className="px-2 py-0.5 text-[11px] font-medium rounded-full whitespace-nowrap text-black opacity-80"
                style={{
                  border: `1.5px solid ${borderColor}`,
                  backgroundColor: 'transparent'
                }}
              >
                {displayName}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function RunChecklistToolBar({
  node,
  checklist,
  onChangeOfStatusIndicator,
  isSaving,
  isOpen,
  setIsOpen
}: {
  node: RunChecklistTree;
  checklist: RunChecklist;
  onChangeOfStatusIndicator?: (
    statusIndicatorId: string,
    status: boolean,
    selectedRunNode: RunChecklistTree
  ) => void;
  isSaving?: boolean;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}) {
  // Variables

  // States
  // const [isOpen, setIsOpen] = useState<boolean>(false);

  return (
    <>
      {/* List of tabs */}
      <div className="flex gap-2">
        {/* Instructions */}
        {checklist?.id && (
          //  Details
          <div>
            <CLTooltip content="Details" position="right">
              <CLButton
                className="flex justify-center rounded-full w-6 h-6 items-center hover:bg-[#e3eaf1] hover:border border-gray-400"
                onClick={() => setIsOpen(o => !o)}
              >
                <svg
                  className={`transition-transform duration-200 ${
                    isOpen ? 'rotate-90' : ''
                  }`}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 12L10 8L6 4"
                    stroke="black"
                    strokeOpacity="0.8"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </CLButton>
            </CLTooltip>
          </div>
        )}

        {/* Comment Indicator */}
        {/* <div className="flex gap-1 justify-center rounded-full w-6 h-6 items-center">
          <div className='text-xs'>2</div>
          <svg
            className="w-3 h-3"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 10V1C0 0.725 0.0979167 0.489583 0.29375 0.29375C0.489583 0.0979167 0.725 0 1 0H9C9.275 0 9.51042 0.0979167 9.70625 0.29375C9.90208 0.489583 10 0.725 10 1V7C10 7.275 9.90208 7.51042 9.70625 7.70625C9.51042 7.90208 9.275 8 9 8H2L0 10ZM1.575 7H9V1H1V7.5625L1.575 7Z"
              fill="black"
              fill-opacity="0.6"
            />
          </svg>
        </div> */}
      </div>
    </>
  );
}

/**
 * Walks a RunChecklistTree and all of its descendants in pre‑order,
 * returning the first node whose checklist.id appears in builderCustomStatusIndicators.
 */
function findFirstWithCustom(
  tree: RunChecklistTree | undefined,
  customStore: ChecklistStatusIndicator[] = []
): RunChecklistTree | undefined {
  if (!tree) return undefined;
  // If this node itself has custom statuses, return it
  if (
    customStore.some(
      cs => cs.checklist_id === (tree.checklist.id || tree.checklist.task_id)
    )
  ) {
    return tree;
  }
  // Otherwise, recurse into its children in order
  for (const child of tree.children || []) {
    const found = findFirstWithCustom(child, customStore);
    if (found) return found;
  }
  return undefined;
}

// helper: climb up until we find an ancestor (not the tree root) with custom pills
function findNearestAncestorWithCustom(
  node: RunChecklistTree | undefined,
  combinedStore: ChecklistStatusIndicator[]
): RunChecklistTree | undefined {
  // climb from parent → parent → … but stop when parent.parent === undefined (the very top)
  let cursor = node?.parent;
  while (cursor && cursor.parent) {
    const hasPills = combinedStore.some(
      cs => cs.checklist_id === cursor?.checklist.id
    );
    if (hasPills) return cursor;
    cursor = cursor.parent;
  }
  return undefined;
}

/**
 * When the *current* user completes *all* of *their* assigned tasks,
 * send one notification to the assigner.
 */
async function notifyAssignersIfDone(
  tree: RunChecklistTree,
  currentUserId: string, // the user who just completed something
  assignerUserId: string, // the one who should be notified
  insertNotification: (
    options: MutationFunctionOptions<any, OperationVariables>
  ) => Promise<any>,
  projectId: string,
  orgId: string,
  notifiedSet: Set<string>,
  runName: string,
  userMap: Record<string, { displayName?: string; email: string }>,
  runId: string
) {
  // 1) flatten all completions
  const completionMap = new Map<string, boolean>();
  (function walk(nodes: RunChecklistTree[]) {
    for (const n of nodes) {
      completionMap.set(n.checklist.id!, Boolean(n.checklist.is_completed));
      if (n.children.length) walk(n.children);
    }
  })(tree.children);

  // 2) group assignments by user
  const assignmentsByUser: Record<string, string[]> = {};
  for (const a of tree.checklist.workflow_configuration!
    .workflow_assignments!) {
    (assignmentsByUser[a.user_id] ||= []).push(a.task_id);
  }

  // 3) only check the *current* user
  const myTaskIds = assignmentsByUser[currentUserId] || [];
  if (!myTaskIds.length) {
    // no tasks assigned to me, nothing to do
    return;
  }

  // have we already notified once?
  if (notifiedSet.has(currentUserId)) return;

  // 4) check if *all* my tasks are done
  const allDone = myTaskIds.every(tid => completionMap.get(tid));
  if (!allDone) return;

  // 5) mark that we've notified for this user
  notifiedSet.add(currentUserId);

  // 6) fire browser (optional)
  // fireNativeNotification(
  //   'Workflow Tasks Complete',
  //   `${userMap[currentUserId].displayName || userMap[currentUserId].email}` +
  //     ` has completed all their workflow tasks in “${runName}.”`
  // );

  // 7) persist into your notifications table
  await insertNotification({
    variables: {
      user_id: assignerUserId,
      org_id: orgId,
      project_id: projectId,
      event_type: 'task_complete_notify_assigner',
      title: 'Workflow Tasks Completed',
      body: `${
        userMap[currentUserId].displayName || userMap[currentUserId].email
      } has completed their workflow tasks in “${runName}.”`,
      notify_at: new Date().toISOString(),
      is_sent: false,
      created_by: currentUserId,
      run_id: runId // pass the runId to the notification
    }
  });
}
